import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export const LogoShowcase = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);

  const rx = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 120, damping: 15 });
  const glowX = useTransform(mx, [0, 1], ["30%", "70%"]);
  const glowY = useTransform(my, [0, 1], ["30%", "70%"]);
  const logoX = useTransform(mx, [0, 1], [-14, 14]);
  const logoY = useTransform(my, [0, 1], [-10, 10]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      ref={ref}
      data-testid="hero-logo-showcase"
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ perspective: 1000 }}
      className="relative w-full aspect-[4/5] select-none"
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative w-full h-full overflow-hidden bg-[#FAF6EF] border border-[#E5E7EB] shadow-[0_30px_60px_-30px_rgba(5,15,36,0.25)]"
      >
        {/* warm light base */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(155deg,#ffffff 0%,#faf6ef 55%,#f1e9db 100%)" }} />

        {/* moving gold glow follows cursor */}
        <motion.div
          aria-hidden
          className="absolute h-[70%] w-[70%] rounded-full blur-[70px] opacity-70"
          style={{
            left: glowX, top: glowY, x: "-50%", y: "-50%",
            background: "radial-gradient(circle, rgba(212,152,58,0.35) 0%, rgba(212,152,58,0) 70%)",
          }}
        />

        {/* faint grid texture */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(5,15,36,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(5,15,36,.8) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />

        {/* orbiting accent dots */}
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            aria-hidden
            className="absolute rounded-full"
            style={{
              width: 6 - i, height: 6 - i,
              background: i % 2 ? "#D94833" : "#D4983A",
              left: `${18 + i * 26}%`, top: `${20 + i * 18}%`,
            }}
            animate={{ y: [0, -14, 0], opacity: [0.3, 0.9, 0.3] }}
            transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
          />
        ))}

        {/* logo with float + cursor parallax + sheen */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-8" style={{ transform: "translateZ(60px)" }}>
          <motion.div
            style={{ x: logoX, y: logoY }}
            className="relative"
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.img
              src="/next-move-logo.png"
              alt="The Next Move by Rhellex logo"
              data-testid="hero-logo-image"
              className="w-[78%] max-w-[380px] mx-auto drop-shadow-[0_14px_30px_rgba(5,15,36,0.18)]"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            />
            {/* sheen sweep */}
            <motion.div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)",
                mixBlendMode: "overlay",
              }}
              animate={{ x: ["-120%", "120%"] }}
              transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" }}
            />
          </motion.div>

          {/* corrected tagline */}
          <motion.p
            data-testid="hero-logo-tagline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 text-center text-xs sm:text-sm font-medium tracking-[0.12em] text-[#0B1B3B]"
          >
            Strategist. Guide. Mentor. Your partner in progress.
          </motion.p>
        </div>

        {/* corner frame accents */}
        <span aria-hidden className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#D4983A]" />
        <span aria-hidden className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#D4983A]" />
      </motion.div>
    </motion.div>
  );
};
